import AppLogoIcon from '@/components/layout/AppLogoIcon';
import { Icon } from '@/components/layout/Icon';
import { UserInfo } from '@/components/layout/UserInfo';
import { UserProfileCard } from '@/components/layout/UserProfileCard';
import { Button } from '@/components/ui/shadcn/button';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/shadcn/drawer';
import { useSettings } from '@/contexts/SettingsContext';
import { useTranslations } from '@/hooks/useTranslations';
import { type NavGroup, type NavItem, type User } from '@/types';
import { Link, usePage } from '@inertiajs/react';
import { Menu } from 'lucide-react';
import { type ReactNode } from 'react';

interface MobileNavigationDrawerProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    navItems?: NavGroup[];
    user?: User;
    rightNavItems?: NavItem[];
    trigger?: ReactNode;
}

export function MobileNavigationDrawer({ open, onOpenChange, navItems = [], user, rightNavItems, trigger }: MobileNavigationDrawerProps) {
    const { t } = useTranslations();
    const { settings } = useSettings();
    const page = usePage();
    const abilities = ((page as any)?.props?.auth?.abilities || []) as string[]; // eslint-disable-line @typescript-eslint/no-explicit-any
    const currentPath = page.url.split('?')[0] ?? page.url;
    const logoUrl = settings.appLogo ? `/storage/${settings.appLogo}` : null;

    const isActiveItem = (url: string) => {
        const itemPath = url.split('?')[0] ?? url;
        return currentPath === itemPath;
    };

    const defaultTrigger = (
        <Button variant="ghost" size="icon" className="h-8 w-8">
            <Menu className="h-4 w-4" />
        </Button>
    );

    const handleLinkClick = () => {
        onOpenChange(false);
    };

    return (
        <Drawer open={open} onOpenChange={onOpenChange}>
            <DrawerTrigger asChild>{trigger || defaultTrigger}</DrawerTrigger>
            <DrawerContent className="bg-sidebar flex h-full w-full flex-col items-stretch justify-between">
                <DrawerTitle className="sr-only">{t('navigation.menu', 'Navigation Menu')}</DrawerTitle>
                <DrawerHeader className="flex flex-row items-center justify-start px-7 py-4">
                    {logoUrl ? (
                        <img src={logoUrl} alt="Logo" className="h-6 w-auto max-w-[180px] object-contain" />
                    ) : (
                        <AppLogoIcon className="h-6 w-6 fill-current text-black dark:text-white" />
                    )}
                </DrawerHeader>
                <div className="flex h-full flex-1 flex-col space-y-4 overflow-y-auto p-4">
                    <div className="flex h-full flex-col justify-between text-sm">
                        <div className="flex flex-col space-y-1">
                            {navItems.length > 0
                                ? navItems.map((group) => (
                                      <div key={group.title} className="space-y-1">
                                          <div className="text-muted-foreground px-2 py-1.5 text-xs font-semibold">{group.title}</div>
                                          {group.items
                                              .filter((item) => !item.requiredPermission || abilities.includes(item.requiredPermission))
                                              .map((item) => {
                                                  const IconComponent = item.icon;
                                                  return (
                                                      <div key={item.title} className="space-y-1">
                                                          <Link
                                                              href={item.url}
                                                              onClick={handleLinkClick}
                                                              className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                                                                  isActiveItem(item.url)
                                                                      ? 'bg-accent text-accent-foreground'
                                                                      : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                                                              }`}
                                                          >
                                                              {IconComponent && <IconComponent className="h-5 w-5" />}
                                                              <span>{item.title}</span>
                                                          </Link>

                                                          {!!item.children?.length && (
                                                              <div className="ml-5 space-y-1 border-l pl-3">
                                                                  {item.children
                                                                      .filter(
                                                                          (child) =>
                                                                              !child.requiredPermission ||
                                                                              abilities.includes(child.requiredPermission),
                                                                      )
                                                                      .map((child) => (
                                                                          <Link
                                                                              key={`${item.title}-${child.title}`}
                                                                              href={child.url}
                                                                              onClick={handleLinkClick}
                                                                              className={`flex items-center rounded-md px-3 py-1.5 text-sm transition-colors ${
                                                                                  page.url === child.url
                                                                                      ? 'bg-accent text-accent-foreground'
                                                                                      : 'text-foreground hover:bg-accent hover:text-accent-foreground'
                                                                              }`}
                                                                          >
                                                                              <span>{child.title}</span>
                                                                          </Link>
                                                                      ))}
                                                              </div>
                                                          )}
                                                      </div>
                                                  );
                                              })}
                                      </div>
                                  ))
                                : rightNavItems?.map((item) => (
                                      <Link
                                          key={item.title}
                                          href={item.url}
                                          onClick={handleLinkClick}
                                          className="hover:bg-accent flex items-center space-x-2 rounded-md px-3 py-2 font-medium"
                                      >
                                          {item.icon && <Icon iconNode={item.icon} className="h-5 w-5" />}
                                          <span>{item.title === 'Analytics' ? t('navigation.analytics', item.title) : item.title}</span>
                                      </Link>
                                  ))}
                        </div>

                        <div className="mt-auto flex flex-col space-y-4 border-t pt-4">
                            {rightNavItems && rightNavItems.length > 0 && navItems.length > 0 && (
                                <div className="flex flex-col space-y-1">
                                    {rightNavItems.map((item) => (
                                        <a
                                            key={item.title}
                                            href={item.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="hover:bg-accent flex items-center space-x-2 rounded-md px-3 py-2 font-medium"
                                        >
                                            {item.icon && <Icon iconNode={item.icon} className="h-5 w-5" />}
                                            <span>{item.title}</span>
                                        </a>
                                    ))}
                                </div>
                            )}
                            {user && (
                                <UserProfileCard user={user}>
                                    {/* eslint-disable-next-line no-restricted-syntax -- DialogTrigger asChild needs a plain interactive element here */}
                                    <button
                                        type="button"
                                        className="hover:bg-accent flex w-full items-center gap-2 rounded-md p-2 text-left transition-colors"
                                    >
                                        <UserInfo user={user} showEmail={true} />
                                    </button>
                                </UserProfileCard>
                            )}
                        </div>
                    </div>
                </div>
            </DrawerContent>
        </Drawer>
    );
}
